import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import { Button } from '@heroui/react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useState } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import GenericAlert from '@/app/components/generic-alert.tsx';
import { usePageLoading } from '@/app/components/page-loading.tsx';
import { getApiErrorMessage } from '@/app/helpers/api-error.helpers.ts';
import { staffEmailCopy } from '@/app/helpers/staff-email.helpers.ts';
import { useAuth } from '@/app/hooks/auth-provider';
import toast from '@/app/toast.ts';
import { Heading } from '@/primitives/heading.tsx';
import TabsNav from '@/primitives/tabs.tsx';
import {
  useDeleteStaffMemberMutation,
  useFetchStaffMemberQuery,
  usePutStaffMemberMutation,
  useReleaseStaffMemberEmailMutation,
} from '@/slices/staff-api-slice.ts';
import type { EditStaffMemberModel, StaffMemberModel } from '../../../types/staff-member.type.ts';
import { UserTypeEnum } from '../../../types/user-type.ts';
import StaffAccounts from './staff-accounts.tsx';
import StaffFormFields from './staff-form-fields.tsx';

const tabs = [
  { label: 'Staff Member', key: 'staff' },
  { label: 'Accounts', key: 'accounts' },
];

const StaffForm = ({ data }: { data: StaffMemberModel }) => {
  const form = useForm<EditStaffMemberModel>({
    values: {
      staffMember: data,
    },
  });
  const { handleSubmit } = form;
  const [putStaffMember, { isLoading: isUpdatingStaffMember }] = usePutStaffMemberMutation();
  const [deleteStaff, { isLoading: isDeletingStaff }] = useDeleteStaffMemberMutation();
  const [releaseEmail, { isLoading: isReleasingEmail }] = useReleaseStaffMemberEmailMutation();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [releaseConfirmOpen, setReleaseConfirmOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.userType === UserTypeEnum.Admin;
  const canDeleteStaff = isAdmin && !!data.canDelete;
  const canReleaseEmail = isAdmin && !!data.canReleaseEmail;

  const onSubmit: SubmitHandler<EditStaffMemberModel> = async ({ staffMember }) => {
    try {
      await putStaffMember(staffMember).unwrap();
      toast.success('Staff member saved successfully.');
      navigate('/staff');
    } catch {
      toast.error('There was an error while saving staff member.');
    }
  };

  const onDeleteStaff = async () => {
    try {
      await deleteStaff(data.key).unwrap();
      toast.success(staffEmailCopy.deleteSuccess);
      navigate('/staff');
    } catch (error) {
      toast.error(getApiErrorMessage(error, staffEmailCopy.deleteError));
    }
  };

  const onReleaseEmail = async () => {
    try {
      await releaseEmail(data.key).unwrap();
      toast.success(staffEmailCopy.freeEmailSuccess);
      navigate('/staff');
    } catch (error) {
      toast.error(getApiErrorMessage(error, staffEmailCopy.freeEmailError));
    }
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <StaffFormFields existingStaffMember={data} name='staffMember' />
        <div className='flex gap-2 mt-4'>
          <Button
            size='lg'
            type='submit'
            color='primary'
            variant='solid'
            isLoading={isUpdatingStaffMember}
          >
            Save Changes
          </Button>
          <Button
            size='lg'
            type='button'
            color='default'
            variant='bordered'
            onPress={() => navigate(-1)}
          >
            Cancel
          </Button>
          {canReleaseEmail && (
            <Button
              size='lg'
              type='button'
              color='warning'
              variant='bordered'
              isLoading={isReleasingEmail}
              onPress={() => setReleaseConfirmOpen(true)}
            >
              {staffEmailCopy.freeEmailButton}
            </Button>
          )}
          {canDeleteStaff && (
            <Button
              size='lg'
              type='button'
              color='danger'
              variant='bordered'
              isLoading={isDeletingStaff}
              onPress={() => setDeleteConfirmOpen(true)}
            >
              Delete Staff
            </Button>
          )}
        </div>
      </form>
      <GenericAlert
        isOpen={releaseConfirmOpen}
        setIsOpen={setReleaseConfirmOpen}
        title={staffEmailCopy.freeEmailTitle}
        message={staffEmailCopy.freeEmailConfirm(data.firstName, data.lastName)}
        onConfirm={onReleaseEmail}
      />
      <GenericAlert
        isOpen={deleteConfirmOpen}
        setIsOpen={setDeleteConfirmOpen}
        title='Delete Staff Member'
        message={staffEmailCopy.deleteConfirm(data.firstName, data.lastName)}
        onConfirm={onDeleteStaff}
      />
    </FormProvider>
  );
};

export default function EditStaff() {
  const params = useParams();
  const { data, isLoading } = useFetchStaffMemberQuery(params?.key ?? skipToken);
  const navigate = useNavigate();

  const content = data
    ? {
        staff: () => <StaffForm data={data} />,
        accounts: () => <StaffAccounts staff={data as any} />,
      }
    : {};

  const { showPageLoading, hidePageLoading } = usePageLoading();

  useEffect(() => {
    isLoading ? showPageLoading('loading-staff-edit') : hidePageLoading('loading-staff-edit');
  }, [isLoading]);

  return (
    <>
      <div className='flex items-center gap-2 mb-4'>
        <Button isIconOnly variant='flat' color='primary' onPress={() => navigate('/staff')}>
          <ArrowLeftIcon className='size-4' />
        </Button>
        <Heading>Edit Staff</Heading>
      </div>
      <TabsNav content={content} tabs={tabs} />
    </>
  );
}
